import React from 'react';
import { Box, Typography } from '@mui/material';
import { ColorDot } from './Badges';
import {
  BRING_BACK_CHOICE_TITLE, EXCLUDE_CHOICE_TITLE, EXCLUDE_CONFIRM, EXCLUDE_LABEL,
  EXCLUDE_STYLE_CONFIRM, EXCLUDE_VARIANT_CONFIRM, EXCLUDE_VARIANT_LABEL, KEEP_BRING_BACK_CONFIRM,
  KEEP_LABEL, SKIP_BRING_BACK_CONFIRM, SKIP_LABEL, UNKEEP_CONFIRM, UNKEEP_LABEL,
  UNSKIP_BRING_BACK_LABEL, VetoControls, VetoVariant,
} from './exclusions';
import { variantChipLabel } from './format';
import { C } from './tokens';

// The 不要 confirm, shared by every element that can raise one: rack tiles (RackPanel, so both the
// desktop map column and the phone sheet) and the 今天 move cards.
//
// Magnus 2026-09-17: 「i should be able to choose 不要這款 or this specific color or size only」.
// A rack tile IS one colour and one size, and the only verb it offered was the style — he dropped
// 3127, 11 pieces in 30 days, meaning to drop one size. So when the element names a variant the
// panel asks WHICH, and the NARROW verb comes first: it is the recoverable one (選品 keeps a 加回
// chip for it) and the wide one should cost a deliberate extra beat.
//
// Magnus 2026-09-17, same sitting: 「for the ones showing 帶回 allow me to remove so it doesn't show
// or refute making it stay.」 A 帶回 tile is the engine PROPOSING that a piece leaves the rail, and
// the only control it had was 不要這款 — which is agreeing with it, harder. So a 帶回 tile opens a
// different set of verbs in THIS SAME panel rather than growing a second control next to it:
//   今天不做  the day-scoped skip that already exists (scope='today', move_kind='bring_back')
//   留著      the new permanent one — it stays on the rail and the engine stops proposing 帶回
// Cheap-and-reversible first, standing decision second, exactly as 不要這個顏色／尺寸 sits above
// 不要這款. Two different commitments, so two different sentences — one line covering both is the
// same lie the shared veto line was.
//
// It still confirms IN PLACE rather than in a dialog — a modal on a phone in a shop is one tap too
// many, and this is a decision taken while looking at the rail (2026-09-15).

const Choice: React.FC<{
  label: string; note: string; primary: boolean; disabled: boolean;
  ariaLabel: string; dot?: string | null; tone?: string; onClick: () => void;
}> = ({ label, note, primary, disabled, ariaLabel, dot, tone, onClick }) => {
  const colour = tone || C.back;
  return (
    <Box sx={{ mt: '5px' }}>
      <Box
        component="button" type="button" disabled={disabled} onClick={onClick} aria-label={ariaLabel}
        sx={{
          width: '100%', cursor: 'pointer', borderRadius: '6px', py: '6px', px: '6px',
          fontSize: 11.5, fontWeight: 800, lineHeight: 1.2, textAlign: 'center',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '2px',
          ...(primary
            ? { border: 0, bgcolor: colour, color: '#fff' }
            : { border: '1px solid', borderColor: colour, bgcolor: C.card, color: colour }),
          '&:disabled': { opacity: 0.5, cursor: 'default' },
        }}
      >
        {dot !== undefined && <ColorDot name={dot} size={9} />}
        {label}
      </Box>
      <Typography sx={{ fontSize: 10.5, color: C.ink2, lineHeight: 1.35, mt: '2px' }}>{note}</Typography>
    </Box>
  );
};

/** What the tile this panel opened on is currently proposing / already decided. */
export interface BringBackState {
  kept: boolean;
  skippedToday: boolean;
}

interface VetoConfirmProps {
  sku: string;
  // null = this element names only a style, so the panel stays exactly as it was: one verb + 取消.
  variant: VetoVariant | null;
  veto: VetoControls;
  // Non-null on a 帶回 tile (or one already 留著／今天不做): the panel offers the two 帶回 verbs
  // instead of the two 不要 verbs. A 帶回 tile has nothing to veto — it is already leaving.
  bringBack?: BringBackState | null;
  onClose: () => void;
}

const VetoConfirm: React.FC<VetoConfirmProps> = ({ sku, variant, veto, bringBack, onClose }) => {
  // `variants_ready === false` means the table is not migrated. Then there is no narrow verb to
  // offer and the old dialog is the honest one — an option that can only answer 503 is worse.
  const narrow = veto.variantsReady ? variant : null;
  const chip = narrow ? variantChipLabel(narrow) : '';
  const busy = !!veto.pending[sku] || (!!narrow && !!veto.pending[`variant|${sku}|${narrow.variant_id}`])
    || (!!narrow && !!veto.pending[`keep|${sku}|${narrow.variant_id}`]);
  // Every 帶回 verb is keyed on the variant, so without one there is nothing to offer and the tile
  // falls back to the 不要 panel rather than showing buttons that cannot address a row.
  const rail = narrow && bringBack ? { ...bringBack, variant: narrow } : null;
  const title = rail
    ? (rail.kept ? `${KEEP_LABEL}：${chip}` : BRING_BACK_CHOICE_TITLE)
    : (narrow ? `${EXCLUDE_CHOICE_TITLE} ${sku} ${chip}` : `${EXCLUDE_LABEL} ${sku}？`);

  return (
    <Box
      role="group" aria-label={title}
      sx={{
        mt: '5px', bgcolor: rail && rail.kept ? C.goWash : C.backWash, border: '1px solid',
        borderColor: rail && rail.kept ? C.go : C.back, borderRadius: '7px', p: '6px',
      }}
    >
      {rail ? (
        <>
          <Typography
            sx={{
              fontSize: 11.5, color: rail.kept ? C.go : C.back, fontWeight: 800, lineHeight: 1.3,
            }}
          >
            {rail.kept ? `${KEEP_LABEL}中` : BRING_BACK_CHOICE_TITLE}
          </Typography>
          {/* 留著 is reversible from the tile itself as well as from the fold: he should never have
              to remember WHERE he pinned something to be able to unpin it. */}
          {rail.kept ? (
            <Choice
              primary label={UNKEEP_LABEL} note={UNKEEP_CONFIRM} disabled={busy} tone={C.go}
              ariaLabel={`${UNKEEP_LABEL} ${sku} ${chip}`}
              onClick={() => { onClose(); veto.onUnkeep(sku, rail.variant.variant_id); }}
            />
          ) : (
            <>
              {/* Day-scoped first: it is the cheap, self-reversing one. It does NOT stop the piece
                  coming off eventually, and its own line says so. */}
              {rail.skippedToday ? (
                <Choice
                  primary label={UNSKIP_BRING_BACK_LABEL} note="取消今天不做，這件今天還是要帶回。"
                  disabled={busy} ariaLabel={`${UNSKIP_BRING_BACK_LABEL} ${sku} ${chip}`}
                  onClick={() => { onClose(); veto.onUnskipBringBack(sku, rail.variant); }}
                />
              ) : (
                <Choice
                  primary label={SKIP_LABEL} note={SKIP_BRING_BACK_CONFIRM} disabled={busy}
                  ariaLabel={`${SKIP_LABEL} ${sku} ${chip}`}
                  onClick={() => { onClose(); veto.onSkipBringBack(sku, rail.variant); }}
                />
              )}
              <Choice
                primary={false} label={KEEP_LABEL} note={KEEP_BRING_BACK_CONFIRM} disabled={busy}
                tone={C.go} dot={rail.variant.color} ariaLabel={`${KEEP_LABEL} ${sku} ${chip}`}
                onClick={() => { onClose(); veto.onKeep(sku, rail.variant); }}
              />
            </>
          )}
        </>
      ) : narrow ? (
        <>
          <Typography sx={{ fontSize: 11.5, color: C.back, fontWeight: 800, lineHeight: 1.3 }}>
            {EXCLUDE_CHOICE_TITLE}
          </Typography>
          <Choice
            primary label={`不要 ${chip}`} note={EXCLUDE_VARIANT_CONFIRM} disabled={busy}
            dot={narrow.color} ariaLabel={`確定${EXCLUDE_VARIANT_LABEL} ${sku} ${chip}`}
            onClick={() => { onClose(); veto.onVariant(sku, narrow); }}
          />
          <Choice
            primary={false} label={EXCLUDE_LABEL} note={EXCLUDE_STYLE_CONFIRM} disabled={busy}
            ariaLabel={`確定${EXCLUDE_LABEL} ${sku}`}
            onClick={() => { onClose(); veto.onStyle(sku); }}
          />
        </>
      ) : (
        <>
          <Typography sx={{ fontSize: 11, color: C.back, fontWeight: 700, lineHeight: 1.35, mb: '4px' }}>
            {EXCLUDE_CONFIRM}
          </Typography>
          <Box
            component="button" type="button" disabled={busy}
            onClick={() => { onClose(); veto.onStyle(sku); }}
            aria-label={`確定${EXCLUDE_LABEL} ${sku}`}
            sx={{
              width: '100%', border: 0, borderRadius: '6px', bgcolor: C.back, color: '#fff',
              fontSize: 11.5, fontWeight: 800, py: '5px', cursor: 'pointer',
              '&:disabled': { opacity: 0.5, cursor: 'default' },
            }}
          >
            {EXCLUDE_LABEL}
          </Box>
        </>
      )}
      <Box
        component="button" type="button" onClick={onClose} aria-label={`取消 ${sku}`}
        sx={{
          width: '100%', mt: '5px', px: 1, py: '5px', border: '1px solid', borderColor: C.line2,
          borderRadius: '6px', bgcolor: C.card, color: C.ink2, fontSize: 11.5, fontWeight: 800,
          cursor: 'pointer',
        }}
      >
        取消
      </Box>
    </Box>
  );
};

export default VetoConfirm;
